import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { Box, Text, Heading, Button, Spacing, DatePicker, TextField, Autocomplete } from '../../ui';
import { useApi, useAsync, useAuth, useNavigate } from '../../hooks';
import { NavBar, Page, Main, NavBackButton, NavRow, NavGroup, Dialog, AppLoadingOverlay, Chip, CustomerAutocomplete } from '../../components';
import { useEventCallback } from '../../ui/hooks';
import { isNull } from '../../ui/utils';
import { AddCircleIcon, UploadCloudIcon } from '../../components/icons';
import { validateApplicationData } from './validateApplicationData';
import { RowLayout, SectionSeperator } from './components';
import { LocationItem } from './LocationItem';
import { AddressAutocomplete } from './AddressAutocomplete';
import { LocationDialog } from './LocationDialog';
import { CsvUploadModal } from './CsvUploadModal';
import { seedLocationFromPendingEntry } from './PendingLocationsHelpers';
import { ProductNames } from '../../constants';
import { getGMTTimestamp } from '../../utils';

// Compact "Label · Count" pill. We don't reuse the shared `Chip` component here because it runs
// `normalizeCamelCase` on its children, which strips digits and the middle-dot separator.
const CountPill = ({ color, label, count }) => (
  <Box bg={color} borderRadius={6} padX="$1.75" padY="$0.75" justifyContent="center" alignItems="center" flexDirection="row">
    <Text size="xSmall" weight={600} onColor={color} uppercase>
      {label}
    </Text>
    <Text size="xSmall" weight={600} onColor={color} marginLeft={6}>
      {`· ${count}`}
    </Text>
  </Box>
);

// The attached customer of a loaded application, in the shape GET /customers/{id} returns. Region,
// country and coordinates are the application's own, which a save does keep.
const attachedCustomerRecord = (app) => {
  const customer = app.customer || {};
  return {
    name: customer.name,
    federalEIN: customer.federalEIN,
    naicsCode: customer.naicsCode,
    primaryEmail: customer.primaryContact?.email,
    billingEmail: customer.billingContact?.email,
    address: customer.address,
    regionCode: app.regionCode,
    countryCode: app.countryCode,
    lat: app.lat,
    lon: app.lon,
  };
};

export const SubmitApplicationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { applicationId } = useParams();
  const location = useLocation();
  const csvSeed = location.state && location.state.csvSeed;
  const { submitApplication, updateApplication, getApplicationWithId, getProducts, getAgents, getCustomer } = useApi();
  const [productId, setProductId] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [insured, setInsured] = useState('');
  // UND-987: the optional "Existing customer" typeahead. Only meaningful on a fresh submission — a
  // draft being edited already has an attached customer, which only Change (PUT .../customer) can
  // touch — so this never seeds from a loaded draft and its value is dropped from the payload for one.
  const [existingCustomer, setExistingCustomer] = useState(null);
  // The picked customer's full record (GET /customers/{id}). While one is picked, it supplies the
  // client details and those fields are locked; see `lockedByCustomer` below.
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerLoadError, setCustomerLoadError] = useState(null);
  const customerRequest = useRef(0);
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [namedInsureds, setNamedInsureds] = useState([]);
  const [federalEIN, setFederalEIN] = useState('');
  const [naicsCode, setNaicsCode] = useState(null);
  const [numberEmployees, setNumberEmployees] = useState(0);
  const [notes, setNotes] = useState('');
  const [placeId, setPlaceId] = useState(null);
  const [address, setAddress] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState(null);
  const [city, setCity] = useState('');
  const [addressDetailsLoading, setAddressDetailsLoading] = useState(false);
  const [regionCode, setRegionCode] = useState('');
  const [postcode, setPostcode] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [timezone, setTimezone] = useState(null);
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [targetEffectiveAt, setTargetEffectiveAt] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editLocationIndex, setEditLocationIndex] = useState(-1);
  const [deleteLocationIndex, setDeleteLocationIndex] = useState(false);
  // CSV-derived state: rows that failed geocoding live here until the user fixes them.
  const [pendingLocations, setPendingLocations] = useState([]);
  const [editingPendingEntry, setEditingPendingEntry] = useState(null);
  const [reuploadModalOpen, setReuploadModalOpen] = useState(false);
  const [draftLoadError, setDraftLoadError] = useState(null);
  // True once the form has been seeded from a CSV parse — controls visibility of the Re-upload
  // CSV button so manual entries don't accidentally surface it.
  const [isCsvDerived, setIsCsvDerived] = useState(Boolean(csvSeed));

  const seedFromApplication = useCallback((app) => {
    if (!app) return;
    setProductId(app.product?.id || null);
    setAgentId(app.agent?.id || null);
    setInsured(app.insured || '');
    setPrimaryEmail(app.primaryEmail || app.customer?.primaryContact?.email || '');
    setBillingEmail(app.billingEmail || app.customer?.billingContact?.email || '');
    setNamedInsureds(app.namedInsureds || []);
    setFederalEIN(app.federalEIN || app.customer?.federalEIN || '');
    setNaicsCode(app.naicsCode || app.customer?.naicsCode || null);
    setNumberEmployees(app.details?.employees || 0);
    setNotes(app.notes || '');
    setPlaceId(app.placeId || app.customer?.placeId || null);
    setAddress(app.address || app.customer?.address || '');
    setAddress1(app.address1 || app.customer?.address1 || '');
    setAddress2(app.address2 || app.customer?.address2 || null);
    setCity(app.city || app.customer?.city || '');
    setRegionCode(app.regionCode || '');
    setPostcode(app.postcode || app.customer?.postcode || '');
    setCountryCode(app.countryCode || '');
    setTimezone(app.timezone || app.customer?.timezone || null);
    setLat(app.lat ?? null);
    setLon(app.lon ?? null);
    if (app.targetEffectiveAt) setTargetEffectiveAt(app.targetEffectiveAt);
    setLocations(Array.isArray(app.locations) ? app.locations : []);
    setPendingLocations(Array.isArray(app.pendingLocations) ? app.pendingLocations : []);
  }, []);

  // The client details a customer's own record supplies. An empty object clears them all.
  const applyCustomerFields = useCallback((customer) => {
    setInsured(customer.name || '');
    setFederalEIN(customer.federalEIN || '');
    setNaicsCode(customer.naicsCode || null);
    setPrimaryEmail(customer.primaryEmail || '');
    setBillingEmail(customer.billingEmail || '');
    setPlaceId(customer.placeId || null);
    setAddress(customer.address || '');
    setAddress1(customer.address1 || '');
    setAddress2(customer.address2 || null);
    setCity(customer.city || '');
    setRegionCode(customer.regionCode || '');
    setPostcode(customer.postcode || '');
    setCountryCode(customer.countryCode || '');
    setTimezone(customer.timezone || null);
    setLat(customer.lat ?? null);
    setLon(customer.lon ?? null);
  }, []);

  // Picking a customer replaces the client details with that customer's; clearing the picker clears
  // them. A pick superseded by a later one (or by clearing) before its record arrives is ignored.
  const handleExistingCustomerChange = useEventCallback(async (next) => {
    const request = ++customerRequest.current;
    setExistingCustomer(next);
    setPickedCustomer(null);
    setCustomerLoadError(null);
    applyCustomerFields({});
    if (!next) {
      setCustomerLoading(false);
      return;
    }
    setCustomerLoading(true);
    try {
      const customer = await getCustomer(next.customerId);
      if (request !== customerRequest.current) return;
      setPickedCustomer(customer);
      applyCustomerFields(customer);
    } catch (err) {
      if (request !== customerRequest.current) return;
      setExistingCustomer(null);
      setCustomerLoadError(err.message || 'Failed to load the customer.');
    } finally {
      if (request === customerRequest.current) setCustomerLoading(false);
    }
  });

  useEffect(() => {
    if (applicationId) {
      let cancelled = false;
      (async () => {
        try {
          const app = await getApplicationWithId(applicationId);
          if (cancelled) return;
          seedFromApplication(app);
          // An existing application's customer is already attached, and saving it never edits that
          // customer's details, so they lock exactly as a picked customer's do.
          setPickedCustomer(attachedCustomerRecord(app));
        } catch (err) {
          if (!cancelled) setDraftLoadError(err.message || 'Failed to load draft');
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (csvSeed) {
      seedFromApplication(csvSeed);
    }
  }, [applicationId, csvSeed, getApplicationWithId, seedFromApplication]);

  const handleSubmit = useEventCallback(async () => {
    // Promote validated pendingLocations entries into the locations[] payload so the server
    // creates real Location rows from them. Anything still marked pending stays in
    // pendingLocations and triggers the 409 guard server-side.
    const validatedFromPending = pendingLocations.filter((e) => e.status === 'validated').map((e) => e.location);
    const stillPending = pendingLocations.filter((e) => e.status === 'pending');
    const application = {
      id: applicationId,
      // Only on a fresh submission: PUT /applications/{id} never changes an application's customer, so
      // sending this on an edit would silently do nothing rather than reflect a mistaken expectation.
      ...(!applicationId && existingCustomer ? { customerId: existingCustomer.customerId } : null),
      productId: productId,
      insured: insured,
      namedInsureds,
      federalEIN,
      naicsCode,
      primaryEmail,
      billingEmail,
      placeId: placeId,
      address: address,
      address1: address1,
      address2: address2,
      city: city,
      regionCode: regionCode,
      postcode: postcode,
      countryCode: countryCode === 'US' ? 'USA' : countryCode,
      timezone: timezone,
      lat: lat,
      lon: lon,
      locations: [...locations, ...validatedFromPending],
      pendingLocations: stillPending,
      details: {
        employees: numberEmployees,
        loss_history_download_link: '',
      },
      notes: notes,
      targetEffectiveAt: getGMTTimestamp(moment(targetEffectiveAt).format('DD-MM-YYYY')),
      agent: { id: agentId },
    };
    validateApplicationData(application);
    return applicationId ? await updateApplication(application) : await submitApplication(application);
  }, [
    applicationId,
    existingCustomer,
    productId,
    agentId,
    naicsCode,
    insured,
    namedInsureds,
    placeId,
    address,
    address1,
    address2,
    city,
    regionCode,
    postcode,
    countryCode,
    timezone,
    lat,
    lon,
    numberEmployees,
    notes,
    targetEffectiveAt,
    locations,
    pendingLocations,
    submitApplication,
    updateApplication,
  ]);

  const { value: newApplication, execute: submit, status, error } = useAsync(handleSubmit, { immediate: false });

  const errors = useMemo(() => {
    if (error && error.validationError) {
      return error;
    }
    return {};
  }, [error]);

  const unhandledError = status === 'error' && (!error || !error.validationError);

  const { value: products, status: productsStatus } = useAsync(getProducts);
  const { value: agents, status: agentsStatus } = useAsync(getAgents);

  const handleSubmitLocation = (loc) => {
    if (editingPendingEntry) {
      // Mutate the pendingLocations entry in place: it's now validated with the resolved
      // Location. Stays in pendingLocations until Submit promotes it.
      const entryId = editingPendingEntry.id;
      setPendingLocations((entries) =>
        entries.map((e) => (e.id === entryId ? { ...e, status: 'validated', location: loc, error: undefined, partialGeocode: undefined } : e))
      );
      setEditingPendingEntry(null);
    } else if (editLocationIndex >= 0 && editLocationIndex < locations.length) {
      setLocations((locs) => {
        const next = [...locs];
        next[editLocationIndex] = loc;
        return next;
      });
    } else {
      setLocations((locs) => [...locs, loc]);
    }
    if (locationDialogOpen) {
      setLocationDialogOpen(false);
    }
  };

  const discardPendingEntry = (entryId) => {
    setPendingLocations((entries) => entries.filter((e) => e.id !== entryId));
  };

  const reloadFromApplication = (app) => {
    seedFromApplication(app);
    setIsCsvDerived(true);
    setReuploadModalOpen(false);
  };

  const deleteLocationAtIndex = (index) => {
    setDeleteLocationIndex(false);
    if (locationDialogOpen) {
      setLocationDialogOpen(false);
    }
    if (editLocationIndex !== -1) {
      setEditLocationIndex(-1);
    }
    if (index >= 0 && index < locations.length) {
      setLocations((locs) => {
        const next = [...locs];
        next.splice(index, 1);
        return next;
      });
    }
  };

  useEffect(() => {
    if (newApplication && typeof newApplication === 'object' && newApplication.id) {
      navigate.to({ pathname: `/applications/${newApplication.id}`, replace: true });
    }
  }, [newApplication, navigate]);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const startDate = new moment();
  startDate.add(90, 'days');
  startDate.startOf('month');
  const [d, setD] = useState(targetEffectiveAt ? moment(targetEffectiveAt) : startDate);
  useEffect(() => {
    if (d) {
      setTargetEffectiveAt(d.valueOf());
    }
  }, [d, setTargetEffectiveAt]);

  useEffect(() => {
    if (agents) {
      agents.sort((a, b) => {
        if (a.broker.name > b.broker.name) {
          return 1;
        } else if (a.broker.name < b.broker.name) {
          return -1;
        } else if (a.name > b.name) {
          return 1;
        } else if (a.name < b.name) {
          return -1;
        } else {
          return 0;
        }
      });

      agents.forEach((a) => (a.display_name = `${a.broker.name} - ${a.name}`));
      const userAgents = user && user.userId ? agents.filter((a) => a.userId === user.userId) : agents;
      if (userAgents && userAgents.length > 0) {
        setAgentId((currId) => {
          if (!currId) {
            return userAgents[0].id;
          }
          return currId;
        });
      }
    }
  }, [agents, user]);

  const verifiedCount = useMemo(
    () => locations.length + pendingLocations.filter((e) => e.status === 'validated').length,
    [locations, pendingLocations]
  );
  const needsFixCount = useMemo(() => pendingLocations.filter((e) => e.status === 'pending').length, [pendingLocations]);

  // A picked customer's record supplies the client details, so those fields are locked while it is
  // picked (and while it loads). A required field the record has no value for stays open, so the
  // submission can still be completed; `customerKey` names that field in the record.
  const lockedByCustomer = (customerKey) => {
    if (customerLoading) return true;
    if (!pickedCustomer) return false;
    if (!customerKey) return true;
    const supplied = pickedCustomer[customerKey];
    return supplied !== null && supplied !== undefined && supplied !== '';
  };

  const naicsOptions = [
    { label: 'Auto Dealer - New/New and Used', value: '441110' },
    { label: 'Auto Dealer - Used', value: '441120' },
  ];
  const productOptions = products && products !== null ? products : [];
  const agentOptions = agents && agents !== null ? agents : [];
  const currentProduct = (products || []).find((p) => p.id === productId);
  const locationCards = locations.map((loc, i) => (
    <LocationItem
      loc={loc}
      key={`${loc.name}${i}`}
      product={currentProduct}
      onDelete={() => {
        setDeleteLocationIndex(i);
      }}
      onEdit={() => {
        setEditLocationIndex(i);
        setLocationDialogOpen(true);
      }}
    />
  ));

  return (
    <Page>
      <AppLoadingOverlay loading={status === 'pending'} />
      <NavBar>
        <NavRow>
          <NavGroup start>
            <NavBackButton path="/applications" />
          </NavGroup>
        </NavRow>
        {unhandledError || (error && error.validationError) ? (
          <NavRow
            bg={unhandledError ? '$error' : '$white'}
            minHeight="50"
            justifyContent="center"
            padX="$1"
            border={{ bottom: { width: 1, color: '$error' } }}
          >
            <Text my="$1.75" bold color={unhandledError ? '$white' : '$error'} lineHeight={20}>
              {unhandledError
                ? 'An error occured and the application may have not been submitted.'
                : 'There are some errors in the form. Please correct these and try again.'}
            </Text>
          </NavRow>
        ) : null}
      </NavBar>
      <Main
        maxWidth={({ theme }) => theme.breakpoints({ sm: 640, md: '$mainWidth' })}
        padBottom={364}
        alignItems="flex-start"
        padTop={({ theme }) => theme.breakpoints({ sm: '$9', md: '$13' })}
        padX={({ theme }) => theme.breakpoints({ xs: theme.spacing(2), sm: theme.spacing(4), md: theme.spacing(6) })}
      >
        <Box alignItems="flex-start" width="100%">
          <Heading level={2} spaceAfter>
            New Application
          </Heading>
          <Text large>All fields are required unless noted as optional</Text>
          <Spacing vertical={({ theme }) => theme.breakpoints({ sm: 3.5, md: 7 })} />
          <Box flex={1} alignSelf="stretch" gap={0}>
            <SectionSeperator label="Policy Details" />
            <RowLayout zIndex="999999999">
              <Autocomplete
                options={productOptions}
                autoComplete
                autoHighlight
                loading={productsStatus === 'pending'}
                getOptionLabel={(option) => {
                  if (typeof option === 'string' || typeof option === 'number') {
                    const productOption = productOptions.find((p) => p.id === option);
                    if (productOption) {
                      return ProductNames[productOption.name] || productOption.name;
                    }
                  } else if (option) {
                    return ProductNames[option.name] || option.name;
                  } else {
                    return '';
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value || option === value}
                value={productId}
                onChangeValue={(v) => setProductId(v && v.id ? v.id : null)}
                renderInput={(iProps) => (
                  <TextField
                    {...iProps}
                    label="Product"
                    helperText={errors.productId ? errors.productId : 'The insurance product this application is applying for.'}
                    error={errors.productId}
                    placeholder="-- Select a product --"
                    filled
                  />
                )}
                flex="1"
              />
              <Autocomplete
                options={agentOptions}
                autoComplete
                autoHighlight
                loading={agentsStatus === 'pending'}
                getOptionLabel={(option) => {
                  if (typeof option === 'string' || typeof option === 'number') {
                    const agentOption = agentOptions.find((a) => a.id === option);
                    if (agentOption) {
                      return agentOption.display_name;
                    }
                  } else if (option) {
                    return option.display_name;
                  } else {
                    return '';
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value || option === value}
                value={agentId}
                onChangeValue={(v) => setAgentId(v && v.id ? v.id : null)}
                renderInput={(iProps) => (
                  <TextField
                    {...iProps}
                    label="Agent"
                    helperText={errors.agent ? errors.agent : 'The agent submitting this application.'}
                    error={errors.agent}
                    placeholder="-- Select an agent --"
                    filled
                  />
                )}
                flex="1"
              />
            </RowLayout>
            <RowLayout>
              <TextField
                label="Target Effective Date"
                value={moment(targetEffectiveAt).format('MM-DD-YYYY')}
                onPress={(date) => {
                  setDatePickerOpen(true);
                }}
                flex={({ theme }) => theme.breakpoints({ sm: 1, md: 0.5 })}
                helperText={errors.targetEffectiveAt ? errors.targetEffectiveAt : undefined}
                error={errors.targetEffectiveAt}
                hideHelperText={isNull(errors.targetEffectiveAt)}
              />
            </RowLayout>
            <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
              <DatePicker
                date={d}
                onChange={(date) => {
                  setD(date);
                  setDatePickerOpen(false);
                }}
              />
            </Dialog>
            <SectionSeperator label="Client Details" />
            {!applicationId ? (
              <CustomerAutocomplete
                value={existingCustomer}
                onChangeValue={handleExistingCustomerChange}
                label="Existing Customer (Optional)"
                helperText={
                  customerLoadError
                    ? customerLoadError
                    : customerLoading
                    ? 'Loading customer…'
                    : existingCustomer
                    ? "The client details below are this customer's. Clear it to enter a new customer instead."
                    : 'Leave empty to create a new customer for this submission.'
                }
              />
            ) : null}
            <TextField
              label="Insured"
              value={insured}
              onChangeValue={setInsured}
              disabled={lockedByCustomer('name')}
              helperText={errors.insured ? errors.insured : undefined}
              error={errors.insured}
              hideHelperText={isNull(errors.insured)}
            />
            <TextField
              type="list"
              addInputValueOnBlur
              label="Additional Named Insureds (Optional)"
              value={namedInsureds}
              onChangeValue={setNamedInsureds}
              helperText="Press Enter to add another name."
              vertical
            />
            <RowLayout zIndex="99999999">
              <TextField
                label="Federal EIN"
                value={federalEIN}
                onChangeValue={setFederalEIN}
                disabled={lockedByCustomer()}
                flex="1"
                helperText={errors.federalEIN ? errors.federalEIN : undefined}
                error={errors.federalEIN}
                hideHelperText={isNull(errors.federalEIN)}
              />
              <Autocomplete
                options={naicsOptions}
                autoComplete
                autoHighlight
                getOptionLabel={(option) => {
                  if (typeof option === 'string' || typeof option === 'number') {
                    // A customer's record can carry a code that isn't one of these options.
                    return naicsOptions.find((o) => o.value === option)?.label ?? `${option}`;
                  } else if (option) {
                    return option.label;
                  } else {
                    return '';
                  }
                }}
                isOptionEqualToValue={(option, value) => option.value === value || option === value}
                value={naicsCode}
                onChangeValue={(v) => setNaicsCode(v && v.value ? v.value : null)}
                disabled={lockedByCustomer()}
                renderInput={(iProps) => (
                  <TextField
                    {...iProps}
                    label="NAICS Code"
                    error={errors.naicsCode}
                    placeholder="-- Select an industry classification --"
                    filled
                    disabled={lockedByCustomer()}
                  />
                )}
                flex="1"
              />
            </RowLayout>
            <RowLayout>
              <TextField
                label="Primary Email"
                value={primaryEmail}
                onChangeValue={setPrimaryEmail}
                disabled={lockedByCustomer('primaryEmail')}
                flex="1"
                helperText={errors.primaryEmail ? errors.primaryEmail : undefined}
                error={errors.primaryEmail}
                hideHelperText={isNull(errors.primaryEmail)}
              />
              <TextField
                label="Billing Email"
                value={billingEmail}
                onChangeValue={setBillingEmail}
                disabled={lockedByCustomer('billingEmail')}
                flex="1"
                helperText={errors.billingEmail ? errors.billingEmail : undefined}
                error={errors.billingEmail}
                hideHelperText={isNull(errors.billingEmail)}
              />
            </RowLayout>
            <AddressAutocomplete
              address={address}
              setAddress={setAddress}
              onDetailsLoading={setAddressDetailsLoading}
              onDetailsReceived={(details) => {
                setLat(details.lat);
                setLon(details.lon);
                setPostcode(details.postcode);
                setCountryCode(details.countryCode);
                setRegionCode(details.regionCode);
                setTimezone(details.timezone);
                setPlaceId(details.placeId);
                setAddress(details.address);
                setAddress1(details.address1);
                setAddress2(details.address2);
                setCity(details.city);
              }}
              helperText={errors.address ? errors.address : undefined}
              error={errors.address}
              disabled={lockedByCustomer('address')}
            />
            <Box flexDirection="row" gap="8" alignSelf="stretch" opacity={addressDetailsLoading ? 0.5 : 1}>
              <TextField
                label="Region Code"
                value={regionCode}
                onChangeValue={setRegionCode}
                disableAutoComplete
                disableAutoCorrect
                disableSpellCheck
                disableAutoCapitalize
                disabled={addressDetailsLoading || lockedByCustomer('regionCode')}
                flex="1"
                helperText={errors.regionCode ? errors.regionCode : undefined}
                error={errors.regionCode}
                hideHelperText={isNull(errors.regionCode)}
              />
              <TextField
                label="Postcode"
                value={postcode}
                onChangeValue={setPostcode}
                disableAutoComplete
                disableAutoCorrect
                disableSpellCheck
                disableAutoCapitalize
                disabled={addressDetailsLoading || lockedByCustomer('address')}
                flex="1"
                helperText={errors.postcode ? errors.postcode : undefined}
                error={errors.postcode}
                hideHelperText={isNull(errors.postcode)}
              />
              <TextField
                label="Country Code"
                value={countryCode}
                onChangeValue={setCountryCode}
                disableAutoComplete
                disableAutoCorrect
                disableSpellCheck
                disableAutoCapitalize
                disabled={addressDetailsLoading || lockedByCustomer('countryCode')}
                flex="1"
                helperText={errors.countryCode ? errors.countryCode : undefined}
                error={errors.countryCode}
                hideHelperText={isNull(errors.countryCode)}
              />
            </Box>
            <Box flexDirection="row" gap="8" flex="1" alignSelf="stretch" opacity={addressDetailsLoading ? 0.5 : 1}>
              <TextField
                label="Latitude"
                value={lat}
                type="number"
                onChangeValue={setLat}
                disableAutoComplete
                disableAutoCorrect
                disableSpellCheck
                disableAutoCapitalize
                disabled={addressDetailsLoading || lockedByCustomer('lat')}
                flex="1"
                helperText={errors.lat ? errors.lat : undefined}
                error={errors.lat}
                hideHelperText={isNull(errors.lat)}
              />
              <TextField
                label="Longitude"
                value={lon}
                type="number"
                onChangeValue={setLon}
                disableAutoComplete
                disableAutoCorrect
                disableSpellCheck
                disableAutoCapitalize
                disabled={addressDetailsLoading || lockedByCustomer('lon')}
                flex="1"
                helperText={errors.lon ? errors.lon : undefined}
                error={errors.lon}
                hideHelperText={isNull(errors.lon)}
              />
            </Box>
            <RowLayout>
              <TextField
                label="Number of Employees"
                value={numberEmployees}
                onChangeValue={setNumberEmployees}
                flex={({ theme }) => theme.breakpoints({ sm: 1, md: 0.5 })}
                type="number"
                precision={0}
                helperText={errors.employees ? errors.employees : undefined}
                error={errors.employees}
                hideHelperText={isNull(errors.employees)}
              />
              <TextField
                label="Notes"
                value={notes}
                onChangeValue={setNotes}
                flex={({ theme }) => theme.breakpoints({ sm: 1, md: 0.5 })}
                multiline={true}
              />
            </RowLayout>

            <SectionSeperator
              lineColor={!isNull(errors.locations) ? '$error' : undefined}
              label={
                <Box flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                  <Box>
                    <Text bold xLarge>
                      Locations
                    </Text>
                    {isCsvDerived || pendingLocations.length > 0 ? (
                      <Box flexDirection="row" gap={6} mt="$1">
                        <CountPill color="$green" label="Verified" count={verifiedCount} />
                        {needsFixCount > 0 && <CountPill color="$orange.main" label="Needs Fix" count={needsFixCount} />}
                        <CountPill color="$gray.300" label="Total" count={verifiedCount + needsFixCount} />
                      </Box>
                    ) : (
                      <Text bold small dim={isNull(errors.locations) ? 0.4 : 1} color={!isNull(errors.locations) ? '$error' : undefined}>
                        {errors.locations
                          ? errors.locations
                          : locationCards.length
                          ? `${locationCards.length} location${locationCards.length > 1 ? 's' : ''} added`
                          : `No locations added`}
                      </Text>
                    )}
                  </Box>
                  {isCsvDerived && !applicationId && (
                    <Button
                      label="Re-upload CSV"
                      variant="outlined"
                      onPress={() => {
                        if (window.confirm('Re-uploading replaces all locations and metadata. Continue?')) {
                          setReuploadModalOpen(true);
                        }
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Spacing vertical={0.75} />
            <Text regular dim={0.8} italic>
              Add locations to be covered under this policy
            </Text>
            <Spacing vertical={5.25} />
            {/* Pending entries first — bubble up un-validated rows so they're easy to find. */}
            {pendingLocations
              .filter((e) => e.status === 'pending')
              .map((entry) => {
                const display = entry.partialGeocode || entry.raw || {};
                const name = display.name || display['Info|Name'] || 'Unnamed location';
                const address = display.address || display['Info|Address'] || '';
                return (
                  <Box key={entry.id} mb="$3" px="$4" py="$3" borderWidth={1} borderColor="$orange.main" borderRadius={8} bg="$orange.light">
                    <Box flexDirection="row" justifyContent="space-between" alignItems="center" mb="$2">
                      <Text bold>{name}</Text>
                      <Chip color="$orange.main">Needs Fix</Chip>
                    </Box>
                    {entry.error && (
                      <Text small color="$coral.bright">
                        {entry.error}
                      </Text>
                    )}
                    <Text small dim>
                      {address}
                    </Text>
                    <Box flexDirection="row" gap={8} mt="$2">
                      <Button
                        label="Fix Location"
                        onPress={() => {
                          setEditingPendingEntry(entry);
                          setLocationDialogOpen(true);
                        }}
                      />
                      <Button label="Discard" variant="secondary" onPress={() => discardPendingEntry(entry.id)} />
                    </Box>
                  </Box>
                );
              })}
            {/* Validated entries from the CSV — render as normal LocationItems but mutate the
                pendingLocations entry on edit/delete so they don't get promoted prematurely. */}
            {pendingLocations
              .filter((e) => e.status === 'validated')
              .map((entry) => (
                <LocationItem
                  loc={entry.location}
                  key={entry.id}
                  product={currentProduct}
                  onDelete={() => discardPendingEntry(entry.id)}
                  onEdit={() => {
                    setEditingPendingEntry(entry);
                    setLocationDialogOpen(true);
                  }}
                />
              ))}
            {locationCards}
            <Spacing vertical={1} />
            <Button
              startIcon={<AddCircleIcon color="$primary" />}
              label="Add Location"
              color="$primary.light"
              outlined
              styles={{
                root: {
                  padY: '$3.5',
                  borderStyle: 'dashed',
                },
                Text: {
                  color: '$primary',
                },
              }}
              large
              onPress={() => {
                if (editLocationIndex !== -1) {
                  setEditLocationIndex(-1);
                }
                setLocationDialogOpen(true);
              }}
            />
            <Spacing vertical={3} />

            <LocationDialog
              open={locationDialogOpen}
              onClose={() => {
                setLocationDialogOpen(false);
                setEditingPendingEntry(null);
              }}
              product={currentProduct}
              onSubmit={handleSubmitLocation}
              canEdit
              mode={editingPendingEntry || (editLocationIndex >= 0 && editLocationIndex < locations.length) ? 'EDIT' : 'CREATE'}
              data={
                editingPendingEntry
                  ? editingPendingEntry.status === 'validated' && editingPendingEntry.location
                    ? editingPendingEntry.location
                    : seedLocationFromPendingEntry(editingPendingEntry)
                  : editLocationIndex >= 0 && editLocationIndex < locations.length
                  ? locations[editLocationIndex]
                  : undefined
              }
              disableBackdropPress={true}
              locations={locations}
              saveButtonLabel={editingPendingEntry ? 'Save Fixed Location' : undefined}
            />

            <CsvUploadModal
              open={reuploadModalOpen}
              onClose={() => setReuploadModalOpen(false)}
              mode="replace"
              productId={productId}
              onSuccess={reloadFromApplication}
            />
          </Box>
          <Spacing vertical={6} />
          <SectionSeperator />
          <Box padY="$1" height={80} alignItems="center" alignSelf="stretch" pointerEvents="box-none" opacity="1">
            <Button
              loading={status === 'pending'}
              large
              onPress={submit}
              minWidth={240}
              height="100%"
              disabled={addressDetailsLoading}
              color={addressDetailsLoading ? '$gray.400' : '$primary'}
              startIcon={<UploadCloudIcon color="$white" />}
            >
              Submit Application
            </Button>
          </Box>
        </Box>
        <DeleteLocationPrompt
          open={deleteLocationIndex !== false}
          onDelete={() => deleteLocationAtIndex(deleteLocationIndex)}
          onCancel={() => setDeleteLocationIndex(false)}
        />
      </Main>
    </Page>
  );
};

const DeleteLocationPrompt = ({ open, onDelete, onCancel }) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      heading="Delete Location"
      prompt="Please confirm you would like to delete this location"
      actions={[
        <Button variant="text" color="$primary" label="Cancel" onPress={onCancel} key="cancel" />,
        <Button label="Delete" onPress={onDelete} key="delete" />,
      ]}
    />
  );
};
